import type { HTMLAttributes, ReactNode } from "react";

import { Inline } from "./Layout";
import { Heading, Text } from "./Typography";

type PageVariant = "config" | "workspace";

/**
 * Page container — the two explicit container modes of UI-LAW §4b.6.
 *
 * `config` (default): the centered settings/authoring column — capped at
 * `--marker-container-max` and horizontally centered inside the shell
 * content region.
 *
 * `workspace`: the full-bleed analysis mode — a full-height flex column so
 * a child (fill-mode `DataTable`, `SplitPane`) can own the remaining
 * viewport height and its own scroll (§4b.3: chrome fixed, content
 * scrolls).
 */
export interface PageProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  variant?: PageVariant;
}

export function Page({
  children,
  className,
  variant = "config",
  ...props
}: PageProps) {
  const classes = ["marker-page", `marker-page--${variant}`];
  if (className) {
    classes.push(className);
  }

  return (
    <section className={classes.join(" ")} {...props}>
      {children}
    </section>
  );
}

// The shell top-bar breadcrumb is the default page identity (UI-LAW §2.11).
// `title` is reserved for dynamic Dossier identity and the ratified page-level
// HelpTip exception, where the HelpTip sits beside this Heading rather than in
// `actions`.
// `leading` is for left-aligned controls such as the dashboard board selector;
// it is never static page identity.
// `tabs` is the Tabbed List archetype's top-level `Tabs` strip (UI-LAW §4b.1):
// it shares the chrome row with `actions` rather than stacking beneath it, so
// a tabbed list spends one chrome row instead of two and its tab strip starts
// at the top-left of the content area instead of below an empty half-row.
export interface PageHeaderProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "title"
> {
  actions?: ReactNode;
  /** Tighten the following content gap for control-only list chrome. */
  compact?: boolean;
  description?: ReactNode;
  /** A `HelpTip` rendered beside the page Heading (UI-LAW §4d). */
  help?: ReactNode;
  leading?: ReactNode;
  /** The page's top-level `Tabs` strip, sharing the chrome row with `actions`. */
  tabs?: ReactNode;
  title?: ReactNode;
}

export function PageHeader({
  actions,
  className,
  compact = false,
  description,
  help,
  leading,
  tabs,
  title,
  ...props
}: PageHeaderProps) {
  // §4b.1 keeps one row of chrome, but a header carrying only `actions` has
  // nothing on its left: the row is a single right-aligned button with ~1200px
  // of empty space beside it, and the container's 24px stack gap then reserves
  // a separator band above the filter bar. The modifier lets the stylesheet
  // fold that actions-only row onto the row beneath it so the action and the
  // filter bar read as one chrome band, reclaiming 24px of the ~160px
  // above-the-fold budget (§4b.2). The empty `__lead` div stays rendered — it
  // is what keeps `justify-content: space-between` anchoring actions right.
  const hasLead =
    Boolean(leading) || Boolean(title) || Boolean(description) || Boolean(help);
  const classes = ["marker-page-header"];
  if (tabs) {
    // The tab strip carries the row's left half, so the actions-only fold
    // below does not apply and must not: there is no row beneath to fold onto.
    classes.push("marker-page-header--tabbed");
  } else if (!hasLead && !compact) {
    // Compact list chrome already tightens the content gap. Applying the
    // actions-only fold as well would stack both negative margins and pull the
    // table into the action row.
    classes.push("marker-page-header--actions-only");
  }
  if (compact) {
    classes.push("marker-page-header--compact");
  }
  if (className) {
    classes.push(className);
  }

  const lead = (
    <div className="marker-page-header__lead">
      {leading ? (
        <Inline className="marker-page-header__leading" gap={3}>
          {leading}
        </Inline>
      ) : null}
      {title ? (
        <span className="marker-page-header__title-row">
          <Heading level={1} size="md">
            {title}
          </Heading>
          {help}
        </span>
      ) : null}
      {description ? (
        <Text className="marker-page-header__description" variant="muted">
          {description}
        </Text>
      ) : null}
    </div>
  );

  const actionsSlot = actions ? (
    <Inline className="marker-page-header__actions" gap={3}>
      {actions}
    </Inline>
  ) : null;

  if (tabs) {
    return (
      <div className={classes.join(" ")} {...props}>
        {hasLead ? lead : null}
        <div className="marker-page-header__tab-row">
          {tabs}
          {actionsSlot}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.join(" ")} {...props}>
      {lead}
      {actionsSlot}
    </div>
  );
}
